import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { enroll, isEditablePhq, waivePhq } from '../api/w2';
import { W2_TID } from '../constants/testids';
import { SELECTION_ROUTE } from '../selection/routes';
import { useW2Mode } from '../store/w2-mode';
import { W2Shell } from '../components/W2Shell';
import { W2WaiverModal } from '../components/W2WaiverModal';
import { W2Onboarding } from '../W2Onboarding';
import { useW2FlowContext } from './W2FlowLayout';
import { useW2Paths } from './paths';

export const W2PhqRoute: React.FC = () => {
  const { employerToken } = useW2FlowContext();
  const navigate = useNavigate();
  const paths = useW2Paths();
  const submitState = useW2Mode((s) => s.submitState);
  const selectionAvailable = useW2Mode((s) => s.selectionAvailable);

  // Sub-stage within the PHQ: waiver modal (spec screen 2) → questionnaire. The
  // reused Welcome + PHQ only render once the employee gets past the waiver by
  // choosing coverage.
  const [phqStage, setPhqStage] = useState<'waiver' | 'questionnaire'>('waiver');

  // JWT guard (C-1 phq row / C-7.2 consequence): evaluated once at mount, which
  // is strictly AFTER the layout's activation effect — a cold load was just
  // cleared and bounces to login ("refresh restarts at auth", frozen v1 non-goal);
  // an intra-flow arrival has the freshly minted JWT.
  const [hasJwt] = useState(() => Boolean(localStorage.getItem('token')));

  // Authoritative terminal re-check on EVERY entry (finding fe-cx #5). A terminal
  // PHQ is one-shot immutable, so a browser Back into `/phq` after a waiver — or
  // any other re-entry — must NOT reopen the waiver. We verify the server-side
  // status before rendering anything editable. Waived stays on the locked done
  // terminal; Completed leaves the token layout for the selection-stage resolver.
  const [gate, setGate] = useState<'checking' | 'open' | 'error'>(
    hasJwt ? 'checking' : 'open'
  );
  // Bumped by the retry button to re-run the authoritative check.
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!hasJwt) return;
    let cancelled = false;
    setGate('checking');
    (async () => {
      try {
        const { phqStatus } = await enroll(employerToken);
        if (cancelled) return;
        if (phqStatus === 'Waived') {
          // Replace so the terminal entry cannot be Back-navigated to reopen.
          navigate(paths.done, {
            state: { reason: 'returning' },
            replace: true,
          });
          return;
        }
        if (phqStatus === 'Completed') {
          // Absolute, top-level route; SelectionFlowPage uses the existing JWT
          // (not the employer token) to resolve selection eligibility/stage.
          navigate(SELECTION_ROUTE, { replace: true });
          return;
        }
        // ONLY an explicitly decoded mid-flow status opens the editable PHQ; a
        // malformed 2xx is unknown, not "non-terminal".
        setGate(isEditablePhq(phqStatus) ? 'open' : 'error');
      } catch {
        // FAIL CLOSED (fe-cx #2, round 2). An unconfirmed status is NOT a
        // non-terminal one: opening the flow here let an already-Waived employee
        // edit a dead PHQ whenever `enroll` timed out or 5xx'd. Hold a
        // non-editable retry screen instead — the waiver/questionnaire never
        // mount until the server says the employee is genuinely mid-flow.
        if (!cancelled) setGate('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasJwt, employerToken, navigate, paths.done, attempt]);

  // The forked questionnaire submit (in HealthQuestionForm → useW2Mode.completePhq)
  // flips submitState to 'done'; that is the questionnaire's terminal end state.
  // When the submit response said selection is open (added-to-Active cohort,
  // phqland-s4 §3.2), hop straight to the selection resolver instead of the
  // thank-you terminal — same destination the Completed re-entry gate uses.
  // Replace history so Back can't reopen the questionnaire after completion.
  useEffect(() => {
    if (submitState === 'done') {
      if (selectionAvailable) {
        navigate(SELECTION_ROUTE, { replace: true });
      } else {
        navigate(paths.done, { state: { reason: 'complete' }, replace: true });
      }
    }
  }, [submitState, selectionAvailable, navigate, paths.done]);

  if (!hasJwt) return <Navigate to={paths.login} replace />;

  // Verifying the authoritative terminal status — hold a neutral shell so no
  // editable PHQ stage mounts before we know the employee isn't already terminal.
  if (gate === 'checking') {
    return (
      <W2Shell title="One moment…" showLogout>
        <p className="text-[15px] text-[#5a5a70]">Loading your enrollment…</p>
      </W2Shell>
    );
  }

  // The authoritative status could NOT be confirmed. A terminal PHQ is one-shot
  // immutable, so "unknown" must behave like "possibly terminal": hold this
  // non-editable retry screen rather than opening the waiver/questionnaire on
  // an employee who may already be Completed/Waived (fe-cx #5).
  if (gate === 'error') {
    return (
      <W2Shell
        showLogout
        title="We couldn’t load your enrollment"
        description="Something went wrong on our side. Please try again in a moment."
      >
        <div data-testid={W2_TID.loadError.root}>
          <button
            type="button"
            data-testid={W2_TID.loadError.retry}
            onClick={() => setAttempt((n) => n + 1)}
            className="rounded-full bg-primary px-6 py-3 text-[15px] font-semibold text-white hover:opacity-90"
          >
            Try again
          </button>
        </div>
      </W2Shell>
    );
  }

  // Waiver gate (spec screen 2) precedes the reused Welcome + PHQ. It mounts only
  // inside this phq route, which only renders inside an activated layout, so it
  // can never read `isW2 === false` (isolation invariant).
  if (phqStage === 'waiver') {
    return (
      <W2WaiverModal
        onCoverage={() => setPhqStage('questionnaire')}
        onWaive={async (certificationText) => {
          await waivePhq(employerToken, certificationText);
          // Replace so browser Back after a waiver cannot reopen `/phq` (fe-cx #5).
          navigate(paths.done, {
            state: { reason: 'waive' },
            replace: true,
          });
        }}
      />
    );
  }
  return <W2Onboarding />;
};
